import React, { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Button } from "@chakra-ui/react";
import Vapor from "laravel-vapor";
import MultiFileUpload, { defaultAllowedTypes } from "./Adjunto/MultiFileUpload";
import { useGlobalContext } from "context/GlobalContext";
import { LabelGroup } from "pages/RefProductInter/LabelGroup";

const EnhancedFileUploadManager = ({
  vaporBaseUrl,
  getAuthToken, // Asegúrar de pasar esta función desde el padre
  title = "Subir documentos del proyecto",
  formID,
  postPropertyName = "attachment",
  endpointAttachments,
  handleResponseUpdateForm = () => {},
  allowedFileTypes = defaultAllowedTypes,
}) => {
  const toast = useToast();
  const { requestData } = useGlobalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  // Estado inicializado para metadata
  const [fileData, setFileData] = useState([]);

  // Mutación para guardar en BD (Backend Laravel)
  const { mutate, isPending } = useMutation({
    mutationFn: requestData,
    onSuccess: (res) => {
      handleResponseUpdateForm(res);
      if (res?.data?.status?.code === 200) {
        toast({
          title: "Carga Completa",
          description: "Los archivos se han guardado exitosamente.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        // Limpieza tras éxito
        handleCloseModal();
      } else {
        // Manejo de error de negocio (ej: validación backend)
        toast({
          title: "Atención",
          description: res?.data?.status?.message || "Error al guardar en base de datos.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    onError: (error) => {
      console.error("Error en mutación:", error);
      toast({
        title: "Error de Servidor",
        description: "No se pudo conectar con el servidor.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Función de subida a Vapor (S3) optimizada
  const uploadToVapor = useCallback(async (file, onProgress) => {
    try {
      // Obtenemos token fresco
      const token = await getAuthToken();

      return Vapor.store(file, {
        baseURL: vaporBaseUrl,
        headers: { Authorization: `Bearer ${token}` },
        progress: onProgress,
      });
    } catch (error) {
      console.error("Fallo al subir a Vapor:", error);
      throw error; // Propagar error para que MultiFileUpload lo maneje visualmente
    }
  }, [getAuthToken, vaporBaseUrl]);

  // Al seleccionar archivos en el componente hijo
  const handleFileSelect = useCallback((uploadedFiles) => {
    if (uploadedFiles.length > 0) {
      setFiles(uploadedFiles);
      // Inicializar metadata vacía para cada archivo
      setFileData(uploadedFiles.map(() => ({
        other_attachment_description: "",
        other_attachment_date: new Date().toISOString().split('T')[0], // Fecha hoy por defecto
        other_attachment_comment: "",
        other_attachment_number: "",
      })));
      setCurrentFileIndex(0);
      setIsModalOpen(true);
    }
  }, []);

  const handleInputChange = (name, value) => {
    setFileData(prevData => {
      const newData = [...prevData];
      newData[currentFileIndex] = { ...newData[currentFileIndex], [name]: value };
      return newData;
    });
  };

  const handleNextFile = () => {
    if (currentFileIndex < files.length - 1) {
      setCurrentFileIndex(prev => prev + 1);
    } else {
      handleUploadToBackend();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFiles([]);
    setFileData([]);
    setCurrentFileIndex(0);
  };

  // Enviar datos ya subidos a S3 hacia el Backend
  const handleUploadToBackend = () => {
    // Validar ID del formulario
    if (!formID) {
      toast({ title: "Error", description: "Falta el ID del formulario.", status: "error" });
      return;
    }

    // Mapeo robusto
    const formattedFiles = files.map((file, index) => ({
      file: {
        uuid: file.vaporId || file.uuid, // Asegurar compatibilidad de nombres
        bucket: file.bucket,
        key: file.key,
        url: file.url,
        headers: file.headers,
        extension: file.extension,
      },
      name: file.name,
      other_attachment_type: file.extension,
      // Metadata
      ...fileData[index],
    }));

    const formData = new FormData();
    formData.append("id", formID);
    // Serializar el array de objetos como espera el backend (según tu código original)
    formData.append(postPropertyName, JSON.stringify(formattedFiles));

    const requestPayload = {
      data: formData,
      Endpoint: endpointAttachments?.create,
      PropertyBody: "sendFormDataContentPOST", // Asegurar casing correcto (a veces es sensible)
    };

    mutate(requestPayload);
  };

  return (
    <>
      <MultiFileUpload
        onFileSelect={handleFileSelect}
        uploadToVapor={uploadToVapor}
        isUploading={isPending} // Bloquear subida si se está guardando en BD
        title={title}
        allowedFileTypes={allowedFileTypes}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="lg"
        closeOnOverlayClick={false} // Evitar cierres accidentales
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Información del archivo ({currentFileIndex + 1}/{files.length}): <br/>
            <small style={{fontWeight: 'normal'}}>{files[currentFileIndex]?.name}</small>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <LabelGroup
              name="other_attachment_description"
              value={fileData[currentFileIndex]?.other_attachment_description}
              onChange={(e) => handleInputChange("other_attachment_description", e.target.value)}
              tag="Descripción"
              size={100}
              maxiLength="50"
            />
            <LabelGroup
              name="other_attachment_number"
              value={fileData[currentFileIndex]?.other_attachment_number}
              onChange={(e) => handleInputChange("other_attachment_number", e.target.value)}
              tag="Número del anexo"
              size={100}
              maxiLength="50"
            />
            <LabelGroup
              name="other_attachment_date"
              value={fileData[currentFileIndex]?.other_attachment_date}
              onChange={(e) => handleInputChange("other_attachment_date", e.target.value)}
              type="date"
              tag="Fecha del anexo"
              size={100}
              max={new Date().toISOString().split("T")[0]}
            />
            <LabelGroup
              name="other_attachment_comment"
              value={fileData[currentFileIndex]?.other_attachment_comment}
              onChange={(e) => handleInputChange("other_attachment_comment", e.target.value)}
              tag="Comentario"
              size={100}
              maxiLength="50"
            />
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal} isDisabled={isPending}>
              Cancelar Todo
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleNextFile}
              isLoading={isPending}
              loadingText="Guardando..."
            >
              {currentFileIndex < files.length - 1 ? "Siguiente archivo" : "Finalizar y Guardar"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EnhancedFileUploadManager;